import { ICONS, type IconId } from './index';
import { INK, LINE, OUTLINE } from './kit';
import { iconLabel, parseRich } from './rich';

const NS = 'http://www.w3.org/2000/svg';
const built = new Map<IconId, SVGSVGElement>();

function build(id: IconId): SVGSVGElement {
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 32 32');
  svg.setAttribute('focusable', 'false');
  for (const s of ICONS[id]) {
    const p = document.createElementNS(NS, 'path');
    p.setAttribute('d', s.d);
    p.setAttribute('fill', s.fill ?? 'none');
    const stroke = s.stroke === false ? null : (s.stroke ?? INK);
    if (stroke) {
      p.setAttribute('stroke', stroke);
      p.setAttribute('stroke-width', String(s.w ?? (s.fill ? OUTLINE : LINE)));
      p.setAttribute('stroke-linejoin', 'round');
      p.setAttribute('stroke-linecap', s.cap ?? 'round');
    }
    if (s.opacity !== undefined) p.setAttribute('opacity', String(s.opacity));
    svg.append(p);
  }
  return svg;
}

/** An icon as an inline SVG element (sized like the text around it, see `.ico`). */
export function iconEl(id: IconId, cls = ''): SVGSVGElement {
  let base = built.get(id);
  if (!base) {
    base = build(id);
    built.set(id, base);
  }
  const svg = base.cloneNode(true) as SVGSVGElement;
  svg.setAttribute('class', `ico ico-${id}${cls ? ` ${cls}` : ''}`);
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', iconLabel(id));
  return svg;
}

/** Text with `:icon:` tokens, as DOM nodes. */
export function richNodes(text: string): Node[] {
  return parseRich(text).map((p) => (typeof p === 'string' ? document.createTextNode(p) : iconEl(p.icon)));
}

/** What each element was last given, and the plain text that produced (to spot outside edits). */
const shown = new WeakMap<Element, { text: string; plain: string }>();

/**
 * Sets an element's content to text, drawing its `:icon:` tokens as icons. Cheap to call
 * every frame: it only touches the DOM when the text changes.
 */
export function setRich(el: Element, text: string): void {
  const last = shown.get(el);
  if (last && last.text === text && el.textContent === last.plain) return;
  if (!text.includes(':')) {
    el.textContent = text;
    shown.set(el, { text, plain: text });
    return;
  }
  const parts = parseRich(text);
  el.replaceChildren(
    ...parts.map((p) => (typeof p === 'string' ? document.createTextNode(p) : iconEl(p.icon))),
  );
  shown.set(el, { text, plain: parts.filter((p) => typeof p === 'string').join('') });
}
